import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ArticleAssociationsLexiClient, ArticleAssociationType, ArticleForUpdateDto, CreateOrUpdateArticleAssociationDto, GetArticleAssociationDto } from '@lexi-clients/lexi';
import { lastValueFrom } from 'rxjs';
import notify from 'devextreme/ui/notify';
import DataSource from 'devextreme/data/data_source';
import { ArticleListService } from '../../services/article.service';
import { DxDataSourceService } from '../../shared/services/dx-data-source.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { NotificationType } from '../../modules/shared/references/references';

@Component({
    selector: 'app-article-association',
    templateUrl: './article-association.component.html',
    styleUrls: ['./article-association.component.scss'],
    standalone: false
})

export class ArticleAssociationComponent implements OnInit {
  @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
  @Input() articleId: number;

  editingMode = {
    mode: 'row',
    usePopup: true  // La propriété qui contrôle l'utilisation du popup
  };

  articleAssociations: GetArticleAssociationDto[] = [];
  filteredArticleAssociations: GetArticleAssociationDto[] = [];
  articleDataSource: DataSource;

  associationTypes = [
    { id: ArticleAssociationType.accessoires, text: 'Accessoires' },
    { id: ArticleAssociationType.prestations, text: 'Prestations' },
    { id: ArticleAssociationType.picesDtaches, text: 'Pièces détachées' },
    { id: ArticleAssociationType.substitution, text: 'Substitutions' }
  ];

  readonly IdTabTousTypes: number = -1; // ID pour "Tous les types"

  tabsDataSource = [
    { id: this.IdTabTousTypes, text: 'Tous' },
    ...this.associationTypes
  ];
  selectedTabIndex = 0;
  selectedTabId: number = this.IdTabTousTypes; // Commence avec "Tous les types"

  constructor(
    private readonly articleAssociationsService: ArticleAssociationsLexiClient,
    private readonly articleService: ArticleListService,
    private readonly dxDataSourceService: DxDataSourceService,
  ) { }

  async ngOnInit() {
    await this.loadArticleAssociations();
    const additionalParams = new Map();
    this.articleDataSource = this.dxDataSourceService.getDataSourceForSelectBox(this.articleService, additionalParams);
    this.filterAssociations();
  }

  async loadArticleAssociations() {
    try {
      const response = await lastValueFrom(this.articleAssociationsService.getByArticleId(this.articleId));
      this.articleAssociations = response;
      return response;
    } catch (error) {
      console.error("Erreur lors du chargement des associations d'articles:", error);
      notify({closeOnClick: true, message: "Erreur lors du chargement des associations d'articles" }, NotificationType.Error);
    }
  }

  onDeleteClick = (e: any) => {
    const id = e.row.data.id;
    this.deleteArticleAssociation(id);
  }

  async deleteArticleAssociation(id: number) {
    try {
      await lastValueFrom(this.articleAssociationsService._delete(id));
      await this.loadArticleAssociations();
      this.filterAssociations();
      notify({closeOnClick: true, message: "Association d'article supprimée avec succès" }, NotificationType.Success);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'association d'article:", error);
      notify({closeOnClick: true, message: "Erreur lors de la suppression de l'association d'article" }, NotificationType.Error);
    }
  }

  async onRowInserting(e: any) {
    try {
      const data = e.data as CreateOrUpdateArticleAssociationDto;
      data.articleId = this.articleId;
      await lastValueFrom(this.articleAssociationsService.create(data));
      await this.loadArticleAssociations();
      this.filterAssociations();
      notify({closeOnClick: true, message: "Association d'article créée avec succès" }, NotificationType.Success);
    } catch (error) {
      e.cancel = true;
      console.error("Erreur lors de la création de l'association d'article:", error);
      notify({closeOnClick: true, message: "Erreur lors de la création de l'association d'article" }, NotificationType.Error);
    }
  }

  async onRowUpdating(e: any) {
    console.log("Row Updating", e);
    try {
      const id = e.key;
      const data = {...e.oldData, ...e.newData} as CreateOrUpdateArticleAssociationDto;
      await lastValueFrom(this.articleAssociationsService.update(data));
      await this.loadArticleAssociations();
      this.filterAssociations();
      notify({closeOnClick: true, message: "Association d'article mise à jour avec succès" }, NotificationType.Success);
    } catch (error) {
      e.cancel = true;
      notify({closeOnClick: true, message: "Erreur lors de la mise à jour de l'association d'article" }, NotificationType.Error);
    }
  }

  articleDisplayExpr(article: ArticleForUpdateDto) {
    return article && `${article.codeBo} - ${article.libelleLong}`;
  }

  refreshDataGrid = async () => {
    await this.loadArticleAssociations();
    this.filterAssociations();
  };

  onTabClick(e: any) {
    this.selectedTabIndex = e.itemIndex;
    this.selectedTabId = e.itemData.id;
    this.filterAssociations();
  }

  onInitNewRow = (e: any) => {
    e.data.quantite = 1;

    if (this.selectedTabId !== this.IdTabTousTypes) {  // tabId -1 c'est la tab "Tous"
      e.data.associationType = this.selectedTabId;
    }
  }

  filterAssociations() {
    if (this.selectedTabId === this.IdTabTousTypes) {
      this.filteredArticleAssociations = [...this.articleAssociations];
    } else {
      const associationType = this.selectedTabId as unknown as ArticleAssociationType;
      this.filteredArticleAssociations = this.articleAssociations.filter(
        item => item.associationType === associationType
      );
    }
  }

  onEditorPreparing(e: any) {
    if (e.dataField === 'associationType' || e.dataField === 'articleAssocieId' || e.dataField === 'articleAssocieLibelle') {
      if (e.parentType === 'dataRow' && !e.row.isNewRow) {
        e.editorOptions.readOnly = true;
      } else {
        e.editorOptions.readOnly = false;
        if (e.dataField === 'associationType' && this.selectedTabId !== this.IdTabTousTypes) {
          e.editorOptions.value = this.selectedTabId;
        }
      }
    }

    // Autofocus sur le champ Quantité lors de l'édition, car c'est le seul champ non readonly
    if (e.dataField === 'quantite') {
      if (e.parentType === 'dataRow' && !e.row.isNewRow) {
        e.editorOptions.onInitialized = (args) => {
          setTimeout(() => {
            args.component.focus();
          }, 100);
        };
      }
    }
  }

  onAddingClick = (e) => {
    const instance = this.dataGrid.instance;
    instance.option('editing.mode', 'popup');
    instance.addRow();
  }

  onEditingStart(e: any) {
    const instance = this.dataGrid.instance;
    instance.option('editing.mode', 'row');
  }
}